import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import type { PageDto } from '@/lib/pagination';

export interface PatientMetadata {
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  email?: string;
  address?: string;
  allergies?: string[];
  insurance?: {
    provider: string;
    policy_number: string;
    group_number?: string;
  };
  emergency_contact?: {
    name: string;
    phone: string;
    relationship: string;
  };
}

export interface PatientOut {
  id: string;
  organization_id: string;
  medical_record_number: string;
  patient_source: string | null;
  metadata_: PatientMetadata | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type PaginatedPatients = PageDto<PatientOut>;

export interface PatientCreateData {
  medical_record_number: string;
  patient_source?: string;
  metadata_?: Record<string, unknown>;
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}/patients`;
}

export function usePatients(params?: { limit?: number; offset?: number; search?: string }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['patients', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PaginatedPatients>(orgUrl(orgId!), { params });
      return data;
    },
    enabled: !!orgId,
  });
}

export function usePatient(patientId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    queryKey: ['patients', orgId, patientId],
    queryFn: async () => {
      const { data } = await clientApi.get<PatientOut>(`${orgUrl(orgId!)}/${patientId}`);
      return data;
    },
    enabled: !!orgId && !!patientId,
  });
}

export function useCreatePatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PatientCreateData) => {
      const { data } = await clientApi.post<PatientOut>(orgUrl(orgId!), body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}

export function useDeletePatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/${patientId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}

export function useBulkDeletePatients() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientIds: string[]) => {
      const results = await Promise.allSettled(
        patientIds.map((id) => clientApi.delete(`${orgUrl(orgId!)}/${id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} of ${patientIds.length} patients`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients', orgId] });
    },
  });
}
